import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Kicker, Tag } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";

const PRICES = [
  ["Initial yarn / connection call", "Free", "15 mins", true],
  ["1:1 Narrative Support", "$110", "60 mins"],
  ["Child & Young Person Mentoring", "$100", "60 mins"],
  ["Mentoring + Narrative Practice", "$110", "60 mins"],
  ["Fitness + Mentoring", "$100", "60 mins"],
  ["Foster/Kinship Carer Support", "$110", "60 mins"],
  ["Substance Use / Turning Story support", "$110", "60 mins"],
  ["Family Narrative Session", "$130", "75–90 mins"],
  ["Couples Session", "Fee on enquiry", "per session"],
  ["Family Restoration Support", "$120", "per hour"],
  ["Professional/Case Consultation", "$130", "per hour"],
  ["Community consultation", "From $120", "per hour or quoted package"],
  ["Schools/organisations", "Quoted", "package"],
  ["Training", "Quoted", "separately"],
];

const Pricing = () => (
  <div data-testid="pricing-page">
    <PageHero
      kicker="Pricing & Fees"
      title={"Clear before\nyou commit."}
      lede="Our private fees are intentionally set to be accessible to community. We believe cost shouldn't unnecessarily create another barrier to accessing therapeutic and relational support. Every fee is confirmed with you in writing before support begins — no surprises."
    >
      <div className="mt-8"><Tag tone="ochre">Suggested pricing — confirmed before anything begins</Tag></div>
    </PageHero>

    <section className="px-6 py-14 sm:py-20 bg-cream">
      <div className="max-w-5xl mx-auto">
        <div className="space-y-4">
          {PRICES.map(([t, p, per, free], i) => (
            <Reveal key={t} delay={i * 0.04}>
              <div
                data-testid={`price-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`rounded-[32px] px-8 py-6 flex flex-wrap items-center justify-between gap-4 transition-transform duration-300 hover:-translate-y-1 ${
                  free ? "bg-terracotta text-cream" : "bg-creamdark border border-ochre/25"
                } flex-col sm:flex-row sm:items-center`}
              >
                <div className="flex items-center gap-5">
                  <span className={`font-display italic text-lg ${free ? "text-cream/60" : "text-terracotta/50"}`}>{String(i + 1).padStart(2, "0")}</span>
                  <p className={`font-semibold text-lg ${free ? "" : "text-charcoal"}`}>{t}</p>
                </div>
                <p className="sm:text-right">
                  <span className={`font-display italic text-3xl ${free ? "text-cream" : "text-terracotta"}`}>{p}</span>
                  <span className={`block text-xs uppercase tracking-[0.18em] font-bold ${free ? "text-cream/70" : "text-earth/60"}`}>{per}</span>
                </p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.25}>
          <div data-testid="arabana-discount" className="mt-10 rounded-[36px] bg-terracotta text-cream p-9 sm:p-12 grain relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-56 h-56 bg-cream/10 organic-blob blur-2xl" />
            <div className="relative">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-cream/70 mb-4">For Arabana mob</p>
              <p className="font-display font-light text-2xl sm:text-3xl leading-snug">
                If you identify as Arabana, an automatic community discount applies to Ngawilyi private support fees.
              </p>
              <p className="mt-5 text-cream/85 leading-relaxed max-w-2xl">
                You don't need to explain or justify why you need the reduced rate — just let us know you're
                Arabana when getting in touch. The discount applies to privately paid services, not externally
                funded services such as NDIS, government, school or organisational funding.
              </p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <p className="mt-10 text-sm leading-relaxed text-earth/75 max-w-3xl">
            Private narrative therapy sessions are 1 hour. Mentoring and movement supports are charged per hour.
            Community consultation, schools/organisations and training are quoted to fit the context. Every fee,
            cancellation arrangement and payment option is confirmed with you before support begins.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link data-testid="pricing-request-btn" to="/request-support" className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105">
              Request Support <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link data-testid="pricing-yarn-btn" to="/contact" className="inline-flex items-center gap-2 rounded-full border-2 border-charcoal px-8 py-[14px] text-sm font-bold uppercase tracking-widest text-charcoal transition-colors duration-300 hover:bg-charcoal hover:text-cream">
              Yarn About Fees
            </Link>
          </div>
        </Reveal>
      </div>
    </section>

    <CTABand
      title="Healing through story. Strength through connection."
      text="Cost shouldn't be the reason someone misses out on an Aunty beside them. If fees are a barrier, yarn with us."
      primary={{ to: "/contact", label: "Yarn With Us" }}
      secondary={{ to: "/private-supports", label: "Private Supports" }}
    />
  </div>
);

export default Pricing;
